import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { TenantConfig } from '../types';

function toRoman(n: number) {
  const romans: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
  ];
  let r = '', x = n;
  for (const [v, sym] of romans) { while (x >= v) { r += sym; x -= v; } }
  return r;
}

export default function Navbar({ config }: { config: TenantConfig }) {
  // ordem EXATA do JSON (com fallback)
  const leftIds = config.nav?.left ?? ['tour', 'releases', 'gallery', 'contact'];
  const left = leftIds.map(id => ({
    id,
    label: id.toUpperCase(),
    href: id === 'home' ? '#top' : `#${id}`,
    subtitle: ''
  }));

  // streams na ordem do JSON
  const right = (config.nav?.right ?? []).map(r => ({
    id: r.key, label: r.label.toUpperCase(), href: r.href, subtitle: '', external: true as const
  }));

  // overlay mobile: se tiver config específica usa; senão, usa os left
  const mobileItems = useMemo(() => {
    if (config.nav?.mobile?.items?.length) {
      return config.nav.mobile.items.map((it) => ({
        id: it.id, label: it.label.toUpperCase(), href: it.href, subtitle: it.subtitle ?? '', external: !!it.external
      }));
    }
    return left;
  }, [config, left]);

  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // animação de fechamento suave (espera o animationend do overlay) + callback pós-fechamento
  const closeWithAnim = (after?: () => void) => {
    const el = overlayRef.current as unknown as HTMLElement | null;
    if (!el) { setOpen(false); after && setTimeout(after, 0); return; }
    if (el.classList.contains('closing')) return; // já está fechando

    document.body.classList.add('menu-closing');

    const runAfter = () => { if (after) setTimeout(after, 30); };

    const onEnd = (ev: AnimationEvent) => {
      if (ev.target !== el) return;
      el.removeEventListener('animationend', onEnd);
      el.classList.remove('closing');
      document.body.classList.remove('menu-closing');
      setOpen(false);
      runAfter();
    };

    el.addEventListener('animationend', onEnd);
    el.classList.add('closing');

    // fallback se o animationend não disparar (navegadores antigos)
    window.setTimeout(() => {
      el.removeEventListener('animationend', onEnd);
      if (document.body.classList.contains('menu-closing')) {
        el.classList.remove('closing');
        document.body.classList.remove('menu-closing');
        setOpen(false);
        runAfter();
      }
    }, 420);
  };
  const scrollYRef = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);

    const lock = () => {
      scrollYRef.current = window.scrollY || window.pageYOffset || 0;
      const b = document.body;
      b.classList.add('menu-open');     // mantém estilos existentes (ex.: blur/overlay tokens)
      b.style.position = 'fixed';
      b.style.top = `-${scrollYRef.current}px`;
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
      b.style.overflow = 'hidden';
    };
    const unlock = () => {
      const b = document.body;
      b.classList.remove('menu-open');
      b.style.position = '';
      b.style.top = '';
      b.style.left = '';
      b.style.right = '';
      b.style.width = '';
      b.style.overflow = '';
      window.scrollTo(0, scrollYRef.current || 0);
    };

    if (open) lock(); else unlock();

    return () => {
      window.removeEventListener('keydown', onKey);
      // segurança: se desmontar enquanto aberto, desbloqueia
      if (document.body.style.position === 'fixed') unlock();
    };
  }, [open]);

  // stagger mais lento (também aplica transitionDelay para animação de saída)
  const itemStyle = (idx: number) => ({
    animationDelay: `${0.10 * idx}s`,
    transitionDelay: `${0.04 * idx}s`,
  });

  return (
    <>
      {/* HEADER */}
      <header className="kl-header">
        <div className="kl-container kl-header-grid">
          {/* Nav esquerda (desktop) */}
          <nav className="kl-nav kl-left">
            {left.map((l) => (
              <a key={l.id} href={l.href} className="kl-link">{l.label}</a>
            ))}
          </nav>

          {/* LOGO (linka para topo) */}
          <div className="kl-brand">
            <a href="#top" className="kl-brand-link">
              {config.brand.logo ? (
                <img src={config.brand.logo} alt={config.brand.headerText ?? config.brand.name ?? 'logo'} />
              ) : (
                <span className="kl-brand-text">{config.brand.headerText ?? config.brand.name ?? 'Koloman'}</span>
              )}
            </a>
          </div>

          {/* Nav direita (desktop) */}
          <nav className="kl-nav kl-right">
            {right.map((r) => (
              <a key={r.id} href={r.href} target="_blank" rel="noreferrer" className="kl-link">
                {r.label}
              </a>
            ))}
          </nav>

          {/* Burger (mobile) — mesmo botão que vira X; some quando overlay abre */}
          <button
            className={`kl-burger ${open ? 'kl-burger-global' : ''}`}
            aria-expanded={open || opening}
            aria-controls="kl-menu"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => {
              if (open) {
                closeWithAnim();
              } else if (!opening) {
                setOpening(true);
                window.setTimeout(() => { setOpen(true); setOpening(false); }, 260);
              }
            }}
          >
            <span className="kl-burger-box kl-burger-duo">
              <span className="kl-burger-line kl-burger-a" />
              <span className="kl-burger-line kl-burger-b" />
            </span>
          </button>
        </div>
      </header>

      {/* OVERLAY MOBILE */}
      <aside
        id="kl-menu"
        className={`kl-menu-overlay ${open ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        ref={overlayRef as any}
      >
        {/* Botão X do overlay (mesmo componente), fixo topo-direito */}


        {/* Itens grandes com stagger; linha cresce após a entrada */}
        <div className="kl-menu-items" onClick={(e) => e.stopPropagation()}>
          {mobileItems.map((it, idx) => (
            <a
              key={it.id}
              href={it.href}
              className="kl-mi kl-mi--in kl-mi--line"
              style={itemStyle(idx)}
              {...(it.external ? { target: '_blank', rel: 'noreferrer' } : {})}
              onClick={(e) => {
                if (it.external) return; // deixa abrir em nova aba normalmente
                e.preventDefault();
                const href = it.href || '';
                const id = href.startsWith('#') ? href.slice(1) : '';
                closeWithAnim(() => {
                  const doScroll = () => {
                    if (!id || id === 'top') {
                      window.history.replaceState(null, '', href || '#top');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      return;
                    }
                    const target = document.getElementById(id);
                    if (target) {
                      window.history.replaceState(null, '', href);
                      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      window.location.hash = href; // fallback
                    }
                  };
                  setTimeout(doScroll, 10);
                });
              }}
            >
              <div className="kl-mi-left">
                <span className="kl-mi-num">{toRoman(idx + 1)}</span>
                <span className="kl-mi-title">{it.label}</span>
              </div>
              {it.subtitle ? <span className="kl-mi-sub">{it.subtitle}</span> : <span />}
            </a>
          ))}
        </div>

        {/* Streams no rodapé (base) do overlay, centralizados horizontalmente */}
        <div className="kl-menu-footer" onClick={(e) => e.stopPropagation()}>
          {right.map((r, idx) => (
            <a
              key={r.id}
              href={r.href}
              target="_blank"
              rel="noreferrer"
              className="kl-menu-footlink kl-mi--in"
              style={itemStyle(idx)}
            >
              {r.label}
            </a>
          ))}
        </div>
      </aside>
    </>
  );
}